/* eslint-disable no-bitwise */
import { EmptyGuid } from './../../dto/security-identifiers';
import { ConfiguredPermission } from './../../dto/configured-permission';
import { Permission } from './../../dto/permission';
import { HttpClient } from '@angular/common/http';
import { AuthorizationInfo } from './../../dto/authorization-info';
import { Injectable, Inject } from '@angular/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { Observable, of, combineLatest, ReplaySubject } from 'rxjs';
import {
  retry,
  take,
  map,
  switchMap,
  filter,
  tap,
  shareReplay
} from 'rxjs/operators';
import { Customer } from '../../dto/customer';
import {
  PermissionsSystem,
  CustomersManagement,
  SystemCustomerID
} from '../../dto/security-identifiers';
import { SecurityService } from '../security.service';
import { API_SERVER } from '@pcplus/domain-core';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  constructor(
    auth: AuthenticationService,
    @Inject(API_SERVER) private apiServer: string,  
    private http: HttpClient,
    private security: SecurityService
  ) {
    // this.selectedCustomer$.next(null);
    auth.currentUser$.subscribe((cu) => {
      if (!cu) {
        this.info$.next(null);
      } else {
        this.http
          .get<AuthorizationInfo>(
            this.apiServer + 'api/Authorization/authorizationInfo'
          )
          .pipe(take(1), retry(3))
          .subscribe((info) => {
            this.info$.next(info);
            this.setSelectedCustomer(info.authenticationInfo.customerId);
          });
      }
    });

    this.customersChanged$.next();
    const customersPermission$ = this.hasPermission(Permission.Read, [
      PermissionsSystem,
      CustomersManagement
    ]);
    this.allCustomers$ = combineLatest([
      customersPermission$,      
      this.currentCustomer,
      this.customersChanged$
    ]).pipe(
      switchMap((pt) => {
        if (pt[0] && pt[1] === SystemCustomerID) {
          return this.security.getAllCustomers();
        } else {
          return of(new Array<Customer>());
        }
      }),
      shareReplay(1)
    );
  }

  private info$ = new ReplaySubject<AuthorizationInfo | null>(1);
  private selectedCustomer$ = new ReplaySubject<string | null>(1);
  private customersChanged$ = new ReplaySubject<void>(1);
  private _selectedCustomer$ = this.selectedCustomer$.asObservable();
  public allCustomers$: Observable<Customer[]>;

  private permissionTypes = [
    Permission.Allow,
    Permission.Create,
    Permission.Delete,
    Permission.Deny,
    Permission.Read,
    Permission.Update
  ];

  get currentCustomer() {
    return this.info$.pipe(
      map((info) => (info ? info.authenticationInfo.customerId : EmptyGuid))
    );
  }

  get selectedCustomer() {
    return this._selectedCustomer$;
  }

  get username() {
    return this.info$.pipe(map((info) => info?.authenticationInfo.username));
  }

  get userFullName$() {
    return this.info$.pipe(
      map((info) =>
        info
          ? info.authenticationInfo.firstName +
            ' ' +
            info.authenticationInfo.lastName
          : ''
      )
    );
  }

  public setSelectedCustomer(customer: string | null) {
    this.selectedCustomer$.next(customer);
  }

  public customersChanged() {
    this.customersChanged$.next();
  }

  hasAnyPermission(permissions: Permission[], activity: string[]) {
    return combineLatest(
      permissions.map((pt) => this.hasPermission(pt, activity))
    ).pipe(map((v) => v.some((b) => b)));
  }

  hasPermission(permission: Permission, activity: string[]) {
    return this.info$
      .pipe(
        map((info) => {
          if (!info) {
            return false;
          }
          if (info.authenticationInfo.isAdministrator) {
            return true;
          }
          return this.hasGroupsPermission(
            info,
            info.authenticationInfo.groups,
            permission,
            activity
          );
        })
      );
  }

  isAdministrator(currentUser?: string) {
    // const sub = new ReplaySubject<boolean>(1);
    if (!currentUser) {
      return this.info$
        .pipe(
          map((info) => {
            if (!info) {
              return false;
            }
            return info.authenticationInfo.isAdministrator;
          })
        )
        // .subscribe((r) => sub.next(r));
    } else {
      return this.info$
        .pipe(
          filter(
            (info) => !!info && info.authenticationInfo.username === currentUser
          ),
          map((info) => {
            if (!info) {
              return false;
            }
            return info.authenticationInfo.isAdministrator;
          })
        )
        // .subscribe((r) => sub.next(r));
    }
    // return sub;
  }

  private hasGroupsPermission(
    info: AuthorizationInfo,
    groups: string[],
    permission: Permission,
    activity: string[]
  ) {
    if (!groups) {
      return false;
    }
    return groups.some((g) =>
      this.hasGroupPermission(info, g, permission, activity)
    );
  }

  private hasGroupPermission(
    info: AuthorizationInfo,
    group: string,
    permission: Permission,
    activity: string[]
  ): boolean {
    const cp = this.getGroupPermissions(info, group, activity);
    if (!cp) {
      return false;
    }
    return !!(cp.permission & permission);
  }

  getGroupPermissions(
    info: AuthorizationInfo,
    group: string,
    activity: string[]
  ) {
    if (!info.permissions || !info.permissions.groups) {
      return null;
    }
    const cp = info.permissions.groups[group];
    if (!cp) {
      return null;
    }
    return this.getConfiguredPermission(cp, activity);
  }

  getConfiguredPermission(
    cp: ConfiguredPermission,
    activity: string[] | null
  ): ConfiguredPermission | null {
    if (!activity || activity.length === 0 || !cp.subPermissions) {
      return cp;
    }
    const configured = cp.subPermissions[activity[0]];
    if (!configured) {
      if (cp.propagatePermission) {
        return cp;
      } else {
        return null;
      }
    }
    return this.getConfiguredPermission(configured, activity.slice(1));
  }
}
